/**
 * onboarding.tsx
 *
 * W66.F — First-login onboarding flow.
 * Five steps, 90 seconds or less. Front door for Partner reps and Bryan's invitees.
 *
 * onboarding/pilot-v1 — Adds pilot methodology disclaimer on step 1 for T3 users.
 *
 * LOCKED CANON:
 * - No exclamation points, no emojis.
 * - M-PACT hyphenated, "Checkpoint" not "Gate", em-dash CP labels.
 * - 5-color palette + teal #0F766E for the step indicator active state.
 * - Banned strings: BANT, MEDDIC, HARD-KILL, HARD-STOP, SOFT-STOP, REFRESH,
 *   "People Mapping", "Fit" as CP, "Compelling event", "Amber".
 * - Bryan K. Short, Founder and CEO, Mi6 Group.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "wouter";
import { getSession, getCurrentTenant, api } from "@/lib/session";
import { CP_CATALOG, type CpCatalogEntry } from "@/lib/cp-catalog";

// ── Palette ──────────────────────────────────────────────────────────────
const C = {
  black:     "#0B0B0F",
  softWhite: "#FAFAF7",
  red:       "#DC2626",
  teal:      "#0F766E",    // W66.F step indicator active
  gray200:   "#E5E5E7",
  gray400:   "#8E8E94",
  gray500:   "#6B6B70",
  gray50:    "#F5F5F4",
  white:     "#FFFFFF",
  success:   "#15803D",
} as const;

const TOTAL_STEPS = 5;

// ── Role-display helpers ──────────────────────────────────────────────────
function roleLabel(role: string): string {
  if (role === "ae") return "Account Executive";
  if (role === "bdr") return "Business Development Representative";
  if (role === "manager") return "Manager";
  if (role === "executive") return "Executive";
  if (role === "marketing") return "Marketing";
  if (role === "se") return "Solutions Engineer";
  return role;
}

// ── Role-aware destination after onboarding ───────────────────────────────
function defaultLandingFor(role: string): string {
  if (role === "marketing") return "/marketing";
  if (role === "executive") return "/workspace";
  if (role === "manager") return "/workspace";
  if (role === "bdr") return "/cadence/bdr";
  return "/workspace"; // ae, se, unknown
}

// ── Step 4: role-aware surface tiles ──────────────────────────────────────
interface SurfaceTile {
  label: string;
  desc: string;
  path: string;
  imgAlt: string;
}

function surfacesForRole(role: string): SurfaceTile[] {
  if (role === "bdr") {
    return [
      { label: "Workspace",    desc: "Your daily home — priorities, coaching, and plan status.", path: "/workspace",   imgAlt: "Workspace surface" },
      { label: "Cadence",      desc: "Your prospecting cadence — every step, every contact.",    path: "/cadence/bdr", imgAlt: "Cadence surface"   },
      { label: "Upload Leads", desc: "Import and qualify new prospect contacts.",                path: "/upload-contacts", imgAlt: "Upload Leads surface" },
    ];
  }
  if (role === "manager") {
    return [
      { label: "Workspace", desc: "Your team overview — rep status, coaching queue, and plan.",    path: "/workspace",  imgAlt: "Workspace surface" },
      { label: "Coaching",  desc: "Individual rep coaching sessions — Checkpoint by Checkpoint.",  path: "/manager",    imgAlt: "Coaching surface"  },
      { label: "Forecast",  desc: "Your team's pipeline and forecast rollup.",                     path: "/forecast",   imgAlt: "Forecast surface"  },
    ];
  }
  // AE, exec, marketing, se — default
  return [
    { label: "Workspace", desc: "Your daily home — plan status, account priorities, and coaching.", path: "/workspace", imgAlt: "Workspace surface" },
    { label: "Cadence",   desc: "Your outreach cadence — next touches, due steps, outcomes.",       path: "/cadence",   imgAlt: "Cadence surface"   },
    { label: "Today",     desc: "Today's prescriptive brief — what to do next and why.",            path: "/today",     imgAlt: "Today surface"     },
  ];
}

// ── Step 5: role-aware CTA ────────────────────────────────────────────────
function ctaForRole(role: string): { label: string; path: string } {
  if (role === "bdr") return { label: "Open your Cadence", path: "/cadence/bdr" };
  if (role === "marketing") return { label: "Open Marketing", path: "/marketing" };
  return { label: "Open your Workspace", path: "/workspace" };
}

// ── CP Popup ───────────────────────────────────────────────────────────────
function CpPopup({ cp, onClose }: { cp: CpCatalogEntry; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`${cp.fullName} detail`}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        background: "rgba(11,11,15,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={ref}
        style={{
          background: C.white, borderRadius: 12,
          padding: "28px 28px 24px",
          maxWidth: 440, width: "100%",
          boxShadow: "0 16px 48px rgba(11,11,15,0.18)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.11em", textTransform: "uppercase", color: C.teal, marginBottom: 4 }}>
              Weight: {cp.weight}%
            </div>
            <div style={{ fontSize: 17, fontWeight: 700, color: C.black }}>
              {cp.fullName}
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: C.gray500, fontSize: 20, lineHeight: 1, padding: "2px 4px",
              borderRadius: 4, marginLeft: 12,
            }}
          >
            ×
          </button>
        </div>
        <p style={{ fontSize: 14, color: C.gray500, lineHeight: 1.6, marginBottom: 12 }}>
          {cp.description}
        </p>
        <div style={{ borderTop: `1px solid ${C.gray200}`, paddingTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: C.gray400, marginBottom: 6 }}>
            Key question
          </div>
          <p style={{ fontSize: 13, color: C.black, lineHeight: 1.6 }}>
            {cp.doctrineNote}
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            marginTop: 20, width: "100%", padding: "10px 0",
            background: C.black, color: C.white,
            border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

// ── Step indicator ─────────────────────────────────────────────────────────
function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div
      style={{ display: "flex", alignItems: "center", gap: 6 }}
      aria-label={`Step ${current} of ${total}`}
      role="progressbar"
      aria-valuenow={current}
      aria-valuemin={1}
      aria-valuemax={total}
    >
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          style={{
            width: i + 1 === current ? 24 : 8,
            height: 8, borderRadius: 4,
            background: i + 1 === current ? C.teal : i + 1 < current ? C.teal + "80" : C.gray200,
            transition: "width 0.25s ease, background 0.2s ease",
          }}
        />
      ))}
      <span style={{ fontSize: 12, color: C.gray400, marginLeft: 6, fontVariantNumeric: "tabular-nums" }}>
        {current}/{total}
      </span>
    </div>
  );
}

// ── Skip link ──────────────────────────────────────────────────────────────
function SkipLink({ onSkip }: { onSkip: () => void }) {
  return (
    <button
      onClick={onSkip}
      style={{
        position: "absolute", bottom: 20, right: 24,
        background: "none", border: "none", cursor: "pointer",
        fontSize: 12, color: C.gray400,
        textDecoration: "underline", textUnderlineOffset: "2px",
        padding: "4px 0",
      }}
      aria-label="Skip onboarding"
    >
      Skip
    </button>
  );
}

// ── Pilot disclaimer banner (shown on step 1 for T3 pilot users) ──────────
function PilotDisclaimerBanner() {
  return (
    <div
      role="note"
      aria-label="Pilot program notice"
      style={{
        background: "#FFF7ED",
        border: "1.5px solid #EA580C",
        borderRadius: 8,
        padding: "16px 20px",
        marginBottom: 28,
        maxWidth: 520,
        margin: "0 auto 28px",
        textAlign: "left",
      }}
    >
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#EA580C", marginBottom: 6 }}>
        Restricted Pilot
      </div>
      <p style={{ fontSize: 13, color: C.black, lineHeight: 1.65, margin: 0 }}>
        Mi6 AIP is in restricted pilot. The methodology and field kit are production-ready.
        The platform is iterating toward production. Your feedback shapes what we build next.
      </p>
    </div>
  );
}

// ── Step 1: Welcome ────────────────────────────────────────────────────────
function StepWelcome({ onNext, isPilot }: { onNext: () => void; isPilot: boolean }) {
  return (
    <div style={{ maxWidth: 520, margin: "0 auto", textAlign: "center", padding: "0 16px" }}>
      <div
        style={{
          width: 48, height: 48, borderRadius: "50%",
          background: C.teal, margin: "0 auto 24px",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
        aria-hidden="true"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke={C.white} strokeWidth="1.5" />
          <path d="M8 12l3 3 5-5" stroke={C.white} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.teal, marginBottom: 12 }}>
        Mi6 Group Intelligence Platform
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, color: C.black, marginBottom: 16, lineHeight: 1.3 }}>
        Welcome to Mi6 Group Intelligence Platform
      </h1>
      <p style={{ fontSize: 15, color: C.gray500, lineHeight: 1.7, marginBottom: isPilot ? 20 : 32 }}>
        I am the platform you operate. Here is what to expect in the next 90 seconds.
      </p>
      <p style={{ fontSize: 13, color: C.gray400, marginBottom: isPilot ? 20 : 36 }}>
        You will see your identity, the M-PACT methodology, your daily surfaces, and your first action.
      </p>
      {isPilot && <PilotDisclaimerBanner />}
      <button
        onClick={onNext}
        autoFocus
        style={{
          padding: "12px 40px", background: C.black, color: C.white,
          border: "none", borderRadius: 6, fontSize: 14, fontWeight: 600,
          cursor: "pointer", letterSpacing: "0.01em",
        }}
      >
        Continue
      </button>
    </div>
  );
}

// ── Step 2: Identity ────────────────────────────────────────────────────────
function StepIdentity({
  name, role, onNext,
}: {
  name: string; role: string; onNext: () => void;
}) {
  const [showContact, setShowContact] = useState(false);

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", padding: "0 16px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.teal, marginBottom: 16 }}>
        Identity confirmation
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.black, marginBottom: 24, lineHeight: 1.3 }}>
        Is this right?
      </h2>
      <div style={{
        background: C.white, border: `1px solid ${C.gray200}`,
        borderRadius: 10, padding: "24px 24px",
        marginBottom: 28,
      }}>
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <div
            style={{
              width: 44, height: 44, borderRadius: "50%",
              background: C.teal + "15", border: `1.5px solid ${C.teal}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0, fontSize: 18, fontWeight: 700, color: C.teal,
            }}
            aria-hidden="true"
          >
            {name ? name.charAt(0).toUpperCase() : "?"}
          </div>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.black, marginBottom: 4 }}>
              {name || "—"}
            </div>
            <div style={{ fontSize: 13, color: C.gray500, marginBottom: 4 }}>
              {roleLabel(role)}
            </div>
          </div>
        </div>
      </div>

      {!showContact ? (
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={onNext}
            autoFocus
            style={{
              flex: 1, padding: "11px 0", background: C.black, color: C.white,
              border: "none", borderRadius: 6, fontSize: 14, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Yes, this is me
          </button>
          <button
            onClick={() => setShowContact(true)}
            style={{
              padding: "11px 20px", background: "none", color: C.black,
              border: `1.5px solid ${C.gray200}`, borderRadius: 6, fontSize: 14,
              fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            Contact Bryan
          </button>
        </div>
      ) : (
        <div style={{
          background: C.gray50, border: `1px solid ${C.gray200}`,
          borderRadius: 8, padding: "16px 20px", marginBottom: 16,
        }}>
          <p style={{ fontSize: 14, color: C.black, marginBottom: 8, fontWeight: 600 }}>
            Contact Bryan K. Short — Founder and CEO, Mi6 Group
          </p>
          <a
            href="mailto:bryan.short@mi6group.com"
            style={{ fontSize: 14, color: C.teal, textDecoration: "underline" }}
          >
            bryan.short@mi6group.com
          </a>
          <div style={{ marginTop: 16 }}>
            <button
              onClick={onNext}
              style={{
                padding: "10px 20px", background: C.black, color: C.white,
                border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Continue anyway
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Step 3: The 10 Checkpoints ─────────────────────────────────────────────
function StepCheckpoints({ onNext }: { onNext: () => void }) {
  const [selectedCp, setSelectedCp] = useState<CpCatalogEntry | null>(null);

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: "0 16px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.teal, marginBottom: 12 }}>
        The M-PACT methodology
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.black, marginBottom: 6, lineHeight: 1.3 }}>
        The 10 Checkpoints
      </h2>
      <p style={{ fontSize: 13, color: C.gray500, marginBottom: 20, lineHeight: 1.5 }}>
        Every deal you work advances through 10 Checkpoints — CP1 through CP10. Click any to learn more.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 24 }}>
        {CP_CATALOG.map((cp) => (
          <button
            key={cp.number}
            onClick={() => setSelectedCp(cp)}
            style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "10px 14px",
              background: C.white, border: `1px solid ${C.gray200}`,
              borderRadius: 7, cursor: "pointer", textAlign: "left",
              transition: "border-color 0.15s, background 0.15s",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = C.teal;
              (e.currentTarget as HTMLButtonElement).style.background = C.teal + "08";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = C.gray200;
              (e.currentTarget as HTMLButtonElement).style.background = C.white;
            }}
            aria-label={`View ${cp.fullName} detail`}
          >
            <div
              style={{
                width: 28, height: 28, borderRadius: 6,
                background: C.teal + "18",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 11, fontWeight: 700, color: C.teal, flexShrink: 0,
              }}
              aria-hidden="true"
            >
              {cp.number}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: C.black }}>
                {cp.fullName}
              </span>
              <span style={{ fontSize: 12, color: C.gray400, marginLeft: 8 }}>
                {cp.weight}%
              </span>
            </div>
            <div style={{ fontSize: 12, color: C.gray400, flexShrink: 0 }}>
              ↗
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={onNext}
        style={{
          width: "100%", padding: "11px 0", background: C.black, color: C.white,
          border: "none", borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: "pointer",
        }}
      >
        Continue
      </button>

      {selectedCp && (
        <CpPopup cp={selectedCp} onClose={() => setSelectedCp(null)} />
      )}
    </div>
  );
}

// ── Surface tile mockup (SVG placeholder) ────────────────────────────────
function SurfaceMockup({ label }: { label: string }) {
  return (
    <svg
      viewBox="0 0 200 120"
      style={{ width: "100%", height: "auto", display: "block", borderRadius: "6px 6px 0 0" }}
      aria-hidden="true"
    >
      <rect width="200" height="120" fill="#EFEFEE" />
      <rect x="12" y="14" width="80" height="8" rx="3" fill="#C9C9CC" />
      <rect x="12" y="28" width="56" height="6" rx="3" fill="#E5E5E7" />
      <rect x="12" y="46" width="176" height="36" rx="5" fill="#FFFFFF" stroke="#E5E5E7" strokeWidth="1" />
      <rect x="20" y="54" width="60" height="6" rx="3" fill="#C9C9CC" />
      <rect x="20" y="64" width="100" height="5" rx="3" fill="#E5E5E7" />
      <rect x="12" y="90" width="176" height="18" rx="4" fill="#0F766E" opacity="0.12" />
      <rect x="20" y="95" width="40" height="5" rx="3" fill="#0F766E" opacity="0.5" />
    </svg>
  );
}

// ── Step 4: Your daily surfaces ───────────────────────────────────────────
function StepSurfaces({ role, onNext }: { role: string; onNext: () => void }) {
  const tiles = surfacesForRole(role);

  return (
    <div style={{ maxWidth: 580, margin: "0 auto", padding: "0 16px" }}>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.teal, marginBottom: 12 }}>
        Your daily surfaces
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 700, color: C.black, marginBottom: 6, lineHeight: 1.3 }}>
        Where you will work every day
      </h2>
      <p style={{ fontSize: 13, color: C.gray500, marginBottom: 22, lineHeight: 1.5 }}>
        Three surfaces are configured for your role. Each connects to your Sales Plan and M-PACT methodology.
      </p>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 12,
        marginBottom: 26,
      }}>
        {tiles.map((tile) => (
          <div
            key={tile.path}
            style={{
              background: C.white, border: `1px solid ${C.gray200}`,
              borderRadius: 8, overflow: "hidden",
            }}
          >
            <SurfaceMockup label={tile.label} />
            <div style={{ padding: "12px 12px 14px" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.black, marginBottom: 4 }}>
                {tile.label}
              </div>
              <div style={{ fontSize: 11, color: C.gray500, lineHeight: 1.5 }}>
                {tile.desc}
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={onNext}
        style={{
          width: "100%", padding: "11px 0", background: C.black, color: C.white,
          border: "none", borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: "pointer",
        }}
      >
        Continue
      </button>
    </div>
  );
}

// ── Step 5: Your first action ──────────────────────────────────────────────
function StepAction({
  role,
  name,
  onComplete,
}: {
  role: string;
  name: string;
  onComplete: () => void;
}) {
  const cta = ctaForRole(role);
  const [, navigate] = useLocation();
  const [loading, setLoading] = useState(false);

  async function handleLaunch() {
    setLoading(true);
    try {
      await api("/api/onboarding/complete", { method: "POST" });
    } catch {
      // non-fatal — route anyway
    }
    onComplete();
    navigate(cta.path);
  }

  return (
    <div style={{ maxWidth: 480, margin: "0 auto", textAlign: "center", padding: "0 16px" }}>
      <div
        style={{
          width: 52, height: 52, borderRadius: "50%",
          background: C.success + "15", border: `1.5px solid ${C.success}`,
          margin: "0 auto 20px",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
        aria-hidden="true"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M5 12l5 5L19 7" stroke={C.success} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: C.teal, marginBottom: 12 }}>
        Your first action
      </div>
      <h2 style={{ fontSize: 24, fontWeight: 700, color: C.black, marginBottom: 12, lineHeight: 1.3 }}>
        {name ? `You are ready, ${name.split(" ")[0]}.` : "You are ready."}
      </h2>
      <p style={{ fontSize: 14, color: C.gray500, lineHeight: 1.7, marginBottom: 32 }}>
        The platform is configured for your role. Your Sales Plan, your M-PACT methodology, and your
        daily surfaces are all ready. Start with the action below.
      </p>
      <button
        onClick={handleLaunch}
        disabled={loading}
        autoFocus
        style={{
          padding: "14px 48px", background: C.teal, color: C.white,
          border: "none", borderRadius: 7, fontSize: 15, fontWeight: 700,
          cursor: loading ? "default" : "pointer", letterSpacing: "0.01em",
          opacity: loading ? 0.75 : 1, transition: "opacity 0.15s",
        }}
      >
        {loading ? "Opening…" : cta.label}
      </button>
      <div style={{ marginTop: 16 }}>
        <a
          href={`#${cta.path}`}
          style={{ fontSize: 12, color: C.gray400, textDecoration: "underline", textUnderlineOffset: "2px" }}
        >
          {cta.path}
        </a>
      </div>
    </div>
  );
}

// ── Shell / outer wrapper ──────────────────────────────────────────────────
function OnboardingShell({
  step,
  onBack,
  onSkip,
  children,
}: {
  step: number;
  onBack: (() => void) | null;
  onSkip: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        minHeight: "100dvh",
        background: C.softWhite,
        fontFamily: "Inter, system-ui, -apple-system, sans-serif",
        display: "flex", flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Top bar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        background: C.softWhite, borderBottom: `1px solid ${C.gray200}`,
        padding: "14px 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase", color: C.gray400 }}>
          Mi6 Group · Attilius
        </div>
        <StepIndicator current={step} total={TOTAL_STEPS} />
        {/* spacer */}
        <div style={{ width: 120 }} />
      </div>

      {/* Main content */}
      <main
        style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          padding: "32px 16px 80px",
        }}
        tabIndex={-1}
        id="onboarding-main"
      >
        {children}
      </main>

      {/* Back button */}
      {onBack && step > 1 && (
        <button
          onClick={onBack}
          style={{
            position: "absolute", bottom: 20, left: 24,
            background: "none", border: "none", cursor: "pointer",
            fontSize: 13, color: C.gray500, padding: "4px 0",
            display: "flex", alignItems: "center", gap: 4,
          }}
          aria-label="Go back"
        >
          ← Back
        </button>
      )}

      {/* Skip link — always present but secondary */}
      {step < TOTAL_STEPS && <SkipLink onSkip={onSkip} />}
    </div>
  );
}

// ── Main Onboarding page component ────────────────────────────────────────
export default function OnboardingPage() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState(1);
  const session = getSession();

  const name = session?.name ?? "";
  const role = session?.role ?? "ae";

  // Skip handler — marks skipped + complete, routes to default landing
  const handleSkip = useCallback(async () => {
    try {
      await api("/api/onboarding/skip", { method: "POST" });
    } catch {
      // non-fatal
    }
    navigate(defaultLandingFor(role));
  }, [role, navigate]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight" && step < TOTAL_STEPS) setStep((s) => s + 1);
      if (e.key === "ArrowLeft" && step > 1) setStep((s) => s - 1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [step]);

  const next = () => setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  const back = step > 1 ? () => setStep((s) => s - 1) : null;

  const tenant = getCurrentTenant();
  const isPilot = !!(tenant && (tenant as any).is_pilot);

  return (
    <OnboardingShell step={step} onBack={back} onSkip={handleSkip}>
      {step === 1 && <StepWelcome onNext={next} isPilot={isPilot} />}
      {step === 2 && <StepIdentity name={name} role={role} onNext={next} />}
      {step === 3 && <StepCheckpoints onNext={next} />}
      {step === 4 && <StepSurfaces role={role} onNext={next} />}
      {step === 5 && (
        <StepAction
          role={role}
          name={name}
          onComplete={() => {/* session object doesn't need patching here; server updated the DB */}}
        />
      )}
    </OnboardingShell>
  );
}
